// BIMCORP MX — Insights (static editorial data)
// Add new posts at the top of POSTS. Each post must have a unique slug.
//
// Post body uses a structured `blocks` array so the article renderer can
// produce rich layouts (headings, bullet lists, comparison tables, FAQs)
// while keeping the editorial feel consistent across articles.

export const POSTS = [
  {
    slug: 'business-entities-in-mexico-choosing-the-right-structure',
    title: 'Business Entities in Mexico: Choosing the Right Structure',
    author: 'BIMCORP MX',
    publishedAt: '2026-02-12',
    category: 'Incorporation',
    excerpt:
      'A practical guide to the main legal structures foreign founders should consider before incorporating a company in Mexico.',
    seoTitle: null,
    seoDescription: null,
    blocks: [
      {
        type: 'paragraph',
        text:
          'Before incorporating in Mexico, one of the most consequential decisions you will make is choosing the right legal structure. The entity type you select affects governance, taxation and ownership flexibility. Here is what foreign founders need to know.',
      },

      { type: 'heading', text: 'The Two Main Options' },
      {
        type: 'paragraph',
        text:
          'The vast majority of foreign founders incorporate in Mexico using one of the two structures: the Sociedad Anónima de Capital Variable (S.A. de C.V.) or the Sociedad de Responsabilidad Limitada (S. de R.L. de C.V.) – The Mexican equivalents of a Corporation and an LLC, respectively.',
      },
      {
        type: 'paragraph',
        text:
          'Both limit shareholder liability and allow carriable capital. The “de C.V.” suffix, used by both, stands for variable capital, meaning the company can adjust its capitalization over time without triggering a full public legal process each time.',
      },

      { type: 'heading', text: 'S.A. de C.V. – The Corporation' },
      {
        type: 'paragraph',
        text:
          'The S.A. de C.V. is Mexico’s standard corporate structure and the default choice for companies that expect outside investment, plan to issue stock options or require a formal governance structure from the start.',
      },
      { type: 'paragraph', text: 'Key characteristics:' },
      {
        type: 'bullets',
        items: [
          'Shareholders: Minimum 2 or 1 under the single-member structure.',
          'Governance: Requires a Board of Directors and a Statutory Examiner (Comisario) – a shareholder or an independent oversight figure that institutional investors typically expect.',
          'US tax treatment: Treated as a standalone foreign corporation by the IRS.',
          'Best for: VC backed companies, businesses on an IPO track, or any structure where equity flexibility and formal governance matter.',
        ],
      },

      { type: 'heading', text: 'S. de R.L. de C.V. – The LLC' },
      {
        type: 'paragraph',
        text:
          'The S. de R.L. is operationally simpler and often the preferred vehicle for US-based parent companies or closely held businesses.',
      },
      { type: 'paragraph', text: 'Key characteristics:' },
      {
        type: 'bullets',
        items: [
          'Shareholders: 2 to 50; transfer of ownership interests requires partner consent.',
          'Governance: No Statutory Examiner required. Management structure is more flexible.',
          'US tax treatment: Under IRS “Check-the-box” rules it can elect pass-through treatment, allowing a US parent company to consolidate the Mexican entity’s profits or losses directly.',
          'Best for: Bootstrapped operations, joint ventures, US subsidiaries, and any structure where operational simplicity and cross-border tax efficiency are priorities.',
        ],
      },

      { type: 'heading', text: 'Comparison Table' },
      {
        type: 'table',
        head: ['', 'S.A. de C.V. (Corporation)', 'S. de R.L. de C.V. (LLC)'],
        rows: [
          [
            'Best for',
            'VC-backed companies, IPO track, equity compensation plans.',
            'Bootstrapped operations, JVs, US subsidiaries.',
          ],
          [
            'US tax treatment',
            'Treated as a standalone foreign corporation.',
            'Can elect pass-through (IRS Check-the-Box).',
          ],
          [
            'Shareholders',
            'Minimum 2, shares freely transferable.',
            '2 to 50, transfer restrictions apply.',
          ],
          [
            'Governance',
            'Board of Directors + Statutory Examiner required.',
            'Simpler structure, no Examiner required.',
          ],
          [
            'Mexico corporate tax',
            '30% flat rate (ISR).',
            '30% flat rate (ISR).',
          ],
        ],
      },

      { type: 'heading', text: 'S.A.P.I. de C.V. – The Venture-Optimized Corporation' },
      {
        type: 'paragraph',
        text:
          'The Sociedad Anónima Promotora de Inversión de Capital Variable (S.A.P.I. de C.V.) is a variation of the S.A. designed specifically for companies seeking private equity or venture capital investment. It was introduced to bring Mexican corporate law closer to international investment standards.',
      },
      { type: 'paragraph', text: 'What sets it apart:' },
      {
        type: 'bullets',
        items: [
          'Shareholder agreements with teeth: The S.A.P.I. allows shareholder agreements to include provisions that override default corporate law rules — drag-along and tag-along rights, anti-dilution protections, and veto rights can all be contractually enforced.',
          'Stock buybacks: Unlike the standard S.A., the S.A.P.I. can repurchase its own shares under certain conditions, enabling option plans and secondary transactions.',
          'Preferred share classes: Investors can hold preferred equity with economic rights distinct from common shareholders.',
          'Best for: Startups and growth-stage companies that are raising institutional capital or structuring complex equity arrangements with international investors.',
        ],
      },
      {
        type: 'paragraph',
        text:
          'If you are building a company that will seek venture funding or require investor-friendly governance from the outset, the S.A.P.I. is worth serious consideration.',
      },

      { type: 'heading', text: 'Other Structures Under Mexican Law' },
      {
        type: 'paragraph',
        text:
          'The Ley General de Sociedades Mercantiles (LGSM) — Mexico’s main corporate statute — recognizes several additional entity types, including the Sociedad en Nombre Colectivo, Sociedad en Comandita Simple, Sociedad en Comandita por Acciones, and Sociedad Cooperativa. In practice, these structures are largely obsolete for commercial purposes and are rarely used by foreign investors. The S.A. de C.V., S. de R.L. de C.V., and S.A.P.I. de C.V. cover virtually all use cases.',
      },

      { type: 'heading', text: 'How BIMCORP Fits In' },
      {
        type: 'paragraph',
        text:
          'Choosing the right structure is the first, and one of the most consequential decisions, in the incorporation process. BIMCORP works with foreign founders to identify the entity type that fits their business model, ownership structure, and growth plans before a single document is drafted.',
      },
      {
        type: 'paragraph',
        text:
          'If your situation calls for something beyond a standard structure — complex cross-border setups, multi-entity arrangements, or investor-ready governance frameworks — our Special Project Structuring service is built for exactly that.',
      },

      { type: 'heading', text: 'FAQs' },
      {
        type: 'faq',
        items: [
          {
            q: 'Can a single person incorporate a company in Mexico?',
            a:
              'For the S.A. de C.V. and S. de R.L. de C.V., a minimum of two shareholders is required. The S.A.P.I. de C.V. also requires at least two. Single-member structures are only available under the Sociedad por Acciones Simplificada (S.A.S.), whose total annual revenues are capped at a threshold that can prove too low for certain lines of business.',
          },
          {
            q: 'Can foreign companies own 100% of a Mexican entity?',
            a:
              'Yes, in most sectors. The company’s bylaws must include a “Calvo Clause”, under which foreign shareholders agree to be treated as nationals with respect to their investment and waive the right to seek diplomatic protection over it.',
          },
          {
            q: 'Which structure do most foreign founders choose?',
            a:
              'The S. de R.L. de C.V. is the most common choice for US-based subsidiaries and early-stage operations. The S.A. de C.V. and S.A.P.I. de C.V. are preferred when outside investment or equity compensation is part of the plan.',
          },
          {
            q: 'Can I change structures later?',
            a:
              'Yes, but converting from one entity type to another is a formal legal process that involves notary fees, new public deed registration, and potential tax implications. Getting the structure right from the start is strongly recommended.',
          },
        ],
      },
    ],
  },
];

export const getAllPosts = () =>
  [...POSTS].sort(
    (a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime()
  );

export const getPostBySlug = (slug) =>
  POSTS.find((p) => p.slug === slug) || null;

export const formatPostDate = (iso, lang = 'en') => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(lang === 'es' ? 'es-MX' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch (_e) {
    return iso;
  }
};
